// --- React ---
import React, { useState } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 공통 UI 컴포넌트 ---
import { ModalShell, FormStack, FormField, FormLabel, SelectBox } from '@/components/ui';
import { Button, Text } from '@/components/ui';

// --- 스토어 ---
import { useNotificationStore } from '@/store/notificationStore';
import { useAppStore } from '@/store/loadingStore';

// --- API ---
import { useReassignDevice } from '@/api/device/useDevice';

// --- 유효성 검사 · 에러 파싱 ---
import { parseCompanyApiError } from '@/utils/errors';

// --- 타입 ---
import type { Device } from '@packages/types/device/dto';
import type { SiteResponse } from '@packages/types/site';

// --- 스타일 ---
import styled from '@emotion/styled';

// ==================== 타입 정의 ====================

interface DeviceReassignModalProps {
  isOpen: boolean;
  device: Device | null;
  sites: SiteResponse[];
  onClose: () => void;
  onSuccess: () => void;
}

// ==================== 스타일 컴포넌트 ====================

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]}px;
  margin-bottom: ${({ theme }) => theme.spacing[5]}px;
`;

const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const InfoValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// ==================== 컴포넌트 ====================

const DeviceReassignModal: React.FC<DeviceReassignModalProps> = ({
  isOpen,
  device,
  sites,
  onClose,
  onSuccess,
}) => {
  // ==================== 다국어 ====================
  const { t } = useTranslation();

  // ==================== 스토어 ====================
  const { showSuccess, showError, showWarning } = useNotificationStore();
  const { showLoading, hideLoading } = useAppStore();

  // ==================== API 훅 ====================
  const reassignDeviceMutation = useReassignDevice();

  // ==================== 상태 관리 ====================
  const [selectedNewSiteId, setSelectedNewSiteId] = useState<number | null>(null);

  // ==================== 핸들러 ====================

  /** 모달 닫기 — 선택값 초기화 후 부모 onClose 호출 */
  const handleClose = () => {
    setSelectedNewSiteId(null);
    onClose();
  };

  /**
   * 확인(재배치) 핸들러
   * 1. 디바이스·사이트 선택 여부 검증
   * 2. reassignDevice API 호출
   * 3. 성공/실패 알림
   */
  const handleConfirm = async () => {
    if (!device || !selectedNewSiteId) {
      showWarning(
        i18nKeys.company.device_reassign_select_site,
        i18nKeys.company.device_reassign_select_site
      );
      return;
    }

    try {
      showLoading();
      await reassignDeviceMutation.mutateAsync({
        device_id: device.id,
        new_site_id: selectedNewSiteId,
      });
      showSuccess(
        i18nKeys.company.device_reassign_success,
        i18nKeys.company.device_reassign_success
      );
      setSelectedNewSiteId(null);
      onSuccess();
    } catch (error: unknown) {
      const e = parseCompanyApiError(error);
      showError(e.title, e.message);
    } finally {
      hideLoading();
    }
  };

  // ==================== 파생 데이터 ====================
  const currentSiteName = sites.find(s => s.id === device?.site_id)?.name || '-';
  const availableSites = sites.filter(s => s.id !== device?.site_id);

  // ==================== 렌더링 ====================

  return (
    <ModalShell
      isOpen={isOpen && !!device}
      title={t(i18nKeys.company.device_reassign_title)}
      onClose={handleClose}
      size="md"
      footer={
        <>
          {/* 취소 버튼 */}
          <Button
            variant="tertiary"
            onClick={handleClose}
            disabled={reassignDeviceMutation.isPending}
          >
            {t(i18nKeys.company.cancel)}
          </Button>
          {/* 확인 버튼 */}
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!selectedNewSiteId || reassignDeviceMutation.isPending}
          >
            {reassignDeviceMutation.isPending
              ? t(i18nKeys.company.processing)
              : t(i18nKeys.company.confirm)}
          </Button>
        </>
      }
    >
      {/* 안내 문구 */}
      <Text size="sm" color="textSecondary" style={{ marginBottom: '16px' }}>
        {t(i18nKeys.company.device_reassign_description)}
      </Text>

      <FormStack>
        {/* 디바이스 시리얼 번호 */}
        <InfoRow>
          <InfoLabel>{t(i18nKeys.company.device_serial_number)}</InfoLabel>
          <InfoValue>{device?.serial_number}</InfoValue>
        </InfoRow>

        {/* 현재 사이트 */}
        <InfoRow>
          <InfoLabel>{t(i18nKeys.company.device_reassign_current_site)}</InfoLabel>
          <InfoValue>{currentSiteName}</InfoValue>
        </InfoRow>

        {/* 새 사이트 선택 (필수) */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.device_reassign_new_site)} *</FormLabel>
          <SelectBox
            value={selectedNewSiteId || ''}
            options={[
              { label: t(i18nKeys.company.device_reassign_select_site), value: '' },
              ...availableSites.map(site => ({ label: site.name, value: site.id })),
            ]}
            onChange={option => setSelectedNewSiteId(option.value ? Number(option.value) : null)}
            isEdit
            width="100%"
            align="left"
            size="md"
          />
        </FormField>
      </FormStack>
    </ModalShell>
  );
};

export default DeviceReassignModal;
